import type { Invite, User } from '@gmn-portal/backend';
import type { ColumnDef, OnChangeFn, SortingState } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import { SortableHeader, TableLoadingCell } from '@/shared/components/tables';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';

import { UserTableActions } from './user-table-actions';

interface UserTableProps {
  users: (User | (Invite & { isInvite: true }))[];
  onEdit: (user: User | (Invite & { isInvite: true })) => void;
  onDelete: (user: User | (Invite & { isInvite: true })) => void;
  onResend: (user: User | (Invite & { isInvite: true })) => void;
  isLoading?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

const resolveUserGroupKey = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'key' in value) {
    return String((value as { key?: string }).key ?? '');
  }
  return '';
};

export const UserTable = ({
  users,
  onEdit,
  onDelete,
  onResend,
  isLoading,
  sorting: externalSorting,
  onSortingChange,
}: UserTableProps) => {
  const { t } = useIntl();
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);

  // Use either external or internal sorting state
  const sorting = externalSorting !== undefined ? externalSorting : internalSorting;

  // Create a handler that works with TanStack's OnChangeFn type
  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newValue =
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (onSortingChange) {
      onSortingChange(newValue);
    } else {
      setInternalSorting(newValue);
    }
  };

  const columns: ColumnDef<User | (Invite & { isInvite: true })>[] = [
    {
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      id: 'name',
      header: ({ column }) => <SortableHeader column={column} title={t('common.name')} />,
      cell: ({ row }) => (
        <span className="font-medium text-foreground">
          {row.original.firstName} {row.original.lastName}
        </span>
      ),
      size: 200,
      minSize: 150,
      maxSize: 300,
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <SortableHeader column={column} title={t('auth.email')} />,
      cell: ({ row }) => <span className="text-muted-foreground">{row.getValue('email')}</span>,
      size: 220,
      minSize: 180,
      maxSize: 300,
    },
    {
      accessorFn: (row) => resolveUserGroupKey((row as User).userGroup),
      id: 'userGroup',
      header: ({ column }) => <SortableHeader column={column} title={t('users.group')} />,
      cell: ({ row }) => {
        const groupKey = resolveUserGroupKey((row.original as User).userGroup);
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-foreground border border-border">
            {groupKey ? t(`portalRoles.${groupKey}` as unknown as never) : '-'}
          </span>
        );
      },
      size: 150,
      minSize: 120,
      maxSize: 200,
    },
    {
      id: 'status',
      accessorFn: (row) => ('isInvite' in row && row.isInvite ? 'invited' : 'active'),
      header: ({ column }) => <SortableHeader column={column} title={t('common.status')} />,
      cell: ({ row }) => {
        const status = row.getValue('status');
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
              status === 'active'
                ? 'bg-success-subtle text-success-subtle-foreground border-transparent'
                : 'bg-warning-subtle text-warning-subtle-foreground border-transparent'
            }`}
          >
            {status === 'active' ? t('users.active') : t('users.invited')}
          </span>
        );
      },
      size: 120,
      minSize: 100,
      maxSize: 150,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <div className="flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
          <UserTableActions
            onEdit={() => onEdit(row.original)}
            onDelete={() => onDelete(row.original)}
            onResend={() => onResend(row.original)}
            isInvite={'isInvite' in row.original && row.original.isInvite}
          />
        </div>
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  const columnCount = columns.length;

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: 'onChange',
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableHeaderRow key={headerGroup.id}>
            {headerGroup.headers.map((header, index) => (
              <TableHead
                key={header.id}
                style={{ width: `${header.getSize()}px` }}
                className={
                  index === 0
                    ? 'pl-6'
                    : index === headerGroup.headers.length - 1
                      ? 'pr-6 text-right'
                      : ''
                }
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableHeaderRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableLoadingCell colSpan={columnCount} />
        ) : table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell, index) => (
                <TableCell
                  key={cell.id}
                  style={{ width: `${cell.column.getSize()}px` }}
                  className={
                    index === 0 ? 'pl-6' : index === row.getVisibleCells().length - 1 ? 'pr-6' : ''
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-card">
            <TableCell
              colSpan={columnCount}
              className="p-8 text-center text-muted-foreground text-sm"
            >
              {t('common.noResults')}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
